import React from 'react';
import { SITE_CONTENT } from './constants';
import Navbar from './components/Navbar';
import About from './components/About';
import UpcomingConcerts from './components/UpcomingConcerts';
import Technique from './components/Technique';
import Maestro from './components/Maestro';
import Gallery from './components/Gallery';
import Repertoire from './components/Repertoire';
import MediaSection from './components/MediaSection';
import ContactForm from './components/ContactForm';
import Footer from './components/Footer';

import { Volume2, VolumeX } from 'lucide-react';

const SectionDivider = () => (
  <div className="flex items-center justify-center w-full pt-12 pb-4 bg-midnight">
    <div className="w-1/4 h-px bg-gradient-to-r from-transparent to-gold/30" />
    <div className="mx-6 transform rotate-45 w-2 h-2 border border-gold/50" />
    <div className="w-1/4 h-px bg-gradient-to-l from-transparent to-gold/30" />
  </div>
);

const App: React.FC = () => {
  const [currentPage, setCurrentPage] = React.useState('home');
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const [isFirstVisit, setIsFirstVisit] = React.useState(true);
  const [activeMediaTitle, setActiveMediaTitle] = React.useState<string | null>(null);
  
  // Audio State
  const [isMuted, setIsMuted] = React.useState(false);
  const [hasPlayedOnce, setHasPlayedOnce] = React.useState(false);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  // Inizializzazione Audio (una sola volta)
  React.useEffect(() => {
    const audio = new Audio('https://www.bottegadellevoci.it/Intro.mp3');
    audio.loop = true;
    audio.volume = 0.2; // Volume ulteriormente ridotto al 20%
    audioRef.current = audio;

    return () => {
      audio.pause();
      audioRef.current = null;
    };
  }, []);

  // Gestione riproduzione automatica e stop durante la navigazione
  React.useEffect(() => {
    if (!audioRef.current) return;

    const playAudio = async () => {
      try {
        if (audioRef.current) {
          await audioRef.current.play();
          setHasPlayedOnce(true);
          setIsMuted(false);
        }
      } catch (err) {
        console.log("Autoplay blocked. Waiting for interaction.");
        setIsMuted(true);
      }
    };

    if (currentPage === 'home' && !hasPlayedOnce) {
      playAudio();

      // Audio Unlocker: prova a far partire la musica al primo click ovunque nella pagina
      const unlock = () => {
        if (currentPage === 'home' && !hasPlayedOnce) {
          playAudio();
          window.removeEventListener('click', unlock);
          window.removeEventListener('touchstart', unlock);
        }
      };

      window.addEventListener('click', unlock);
      window.addEventListener('touchstart', unlock);
      return () => {
        window.removeEventListener('click', unlock);
        window.removeEventListener('touchstart', unlock);
      };
    } else if (currentPage !== 'home') {
      audioRef.current.pause();
      setIsMuted(true);
    }
  }, [currentPage, hasPlayedOnce]);

  const toggleMusic = () => {
    if (audioRef.current) {
      if (audioRef.current.paused || isMuted) {
        audioRef.current.play().catch(() => {});
        audioRef.current.muted = false;
        setIsMuted(false);
      } else {
        audioRef.current.pause();
        setIsMuted(true);
      }
    }
  };

  React.useEffect(() => {
    // Segnamo come "visto" dopo che l'animazione è completata (circa 7 secondi totali)
    const timer = setTimeout(() => {
      setIsFirstVisit(false);
    }, 6000);
    return () => clearTimeout(timer);
  }, []);

  const handlePageChange = (pageId: string, mediaTitle?: string) => {
    if (pageId === currentPage && !mediaTitle) return;
    
    setIsTransitioning(true);
    setActiveMediaTitle(mediaTitle || null);
    
    setTimeout(() => {
      setCurrentPage(pageId);
      window.scrollTo(0, 0);
      setIsTransitioning(false);
    }, 400);
  };

  // Costante per lo spazio sotto la navbar: Altezza Navbar + ~2cm
  const contentTopPadding = "pt-[170px] lg:pt-[220px]";

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return (
          <div className="relative">
            {/* SFONDO FISSO */}
            <div className="fixed top-0 left-0 w-full h-screen z-0 bg-midnight">
              <div className="relative w-full h-full overflow-hidden flex items-center justify-center">
                {/* Sfondo sfocato per riempire i bordi su mobile/tablet se l'immagine è in 'contain' */}
                <img 
                  src={SITE_CONTENT.hero.image} 
                  alt="" 
                  className={`absolute inset-0 w-full h-full object-cover blur-3xl opacity-40 lg:hidden ${isFirstVisit ? 'opacity-0 animate-heroImage' : 'opacity-100'}`}
                />
                <img 
                  src={SITE_CONTENT.hero.image} 
                  alt="Bottega delle Voci Background" 
                  className={`relative z-10 w-full h-full object-contain lg:object-cover ${isFirstVisit ? 'opacity-0 animate-heroImage' : 'opacity-100'}`}
                />
                <div className={`absolute inset-0 bg-black/40 z-20 ${isFirstVisit ? 'opacity-0 animate-heroOverlay' : 'opacity-100'}`} />
                <div className={`absolute inset-0 bg-gradient-to-b from-midnight/40 to-midnight/90 z-20 ${isFirstVisit ? 'opacity-0 animate-heroOverlay' : 'opacity-100'}`} />
              </div>
            </div>
            
            <div className="relative z-10">
              {/* HERO CONTENT: Inizia 2cm sotto la Navbar */}
              <section className={`min-h-screen w-full flex flex-col items-center justify-start ${contentTopPadding} px-6 pointer-events-none`}>
                <div className="text-center max-w-7xl mx-auto flex flex-col items-center pointer-events-auto">
                  <div className={isFirstVisit ? 'opacity-0 animate-heroText' : 'opacity-100'}>
                    <p className="text-gold-light uppercase tracking-[0.5em] text-lg md:text-xl mb-6 font-sans drop-shadow-lg">
                      {SITE_CONTENT.hero.overline}
                    </p>
                    <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-brand font-bold text-white mb-8 drop-shadow-2xl tracking-tight">
                      {SITE_CONTENT.hero.title}
                    </h1>
                    <p className="text-xl md:text-2xl text-gold-light mb-12 tracking-wide font-serif italic max-w-2xl mx-auto leading-relaxed drop-shadow-md">
                      {SITE_CONTENT.hero.subtitle}
                    </p>
                    
                    <button 
                      onClick={() => handlePageChange('media')}
                      className="group relative px-14 py-6 overflow-hidden border border-gold/40 bg-transparent text-gold hover:text-midnight transition-colors duration-500 shadow-2xl"
                    >
                      <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                      <span className="relative z-10 uppercase tracking-[0.4em] text-[13px]">{SITE_CONTENT.hero.cta}</span>
                    </button>
                  </div>
                </div>

                {/* TASTO SCORRI */}
                <div 
                  onClick={() => document.getElementById('about-section')?.scrollIntoView({ behavior: 'smooth' })}
                  className={`mt-auto mb-12 flex flex-col items-center group cursor-pointer pointer-events-auto ${isFirstVisit ? 'opacity-0 animate-heroText' : 'opacity-100'}`}
                >
                  <div className="relative flex items-center justify-center w-16 h-16 mb-4">
                    <div className="absolute inset-0 rounded-full border border-gold opacity-30 animate-ping" />
                    <div className="absolute inset-0 rounded-full border-2 border-gold/60 group-hover:border-gold transition-all duration-300 bg-midnight/40 backdrop-blur-md shadow-[0_0_20px_rgba(212,175,55,0.2)]" />
                    <svg className="w-7 h-7 text-gold animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ animationDuration: '2s' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </div>
                  <span className="text-gold text-[11px] uppercase tracking-[0.6em] opacity-80 group-hover:opacity-100 transition-opacity drop-shadow-md">
                    Scorri
                  </span>
                </div>
              </section>
              
              <div className="bg-midnight shadow-[0_-50px_100px_rgba(0,0,0,0.9)] relative z-20">
                <About />
                <SectionDivider />
                <UpcomingConcerts />
              </div>
            </div>
          </div>
        );
      case 'tecnica':
        return <div className={`${contentTopPadding} pb-24 relative z-10 bg-midnight`}><Technique /></div>;
      case 'maestro':
        return <div className={`${contentTopPadding} pb-24 relative z-10 bg-midnight`}><Maestro /></div>;
      case 'gallery':
        return <div className={`${contentTopPadding} pb-24 relative z-10 bg-midnight`}><Gallery /></div>;
      case 'repertorio':
        return <div className={`${contentTopPadding} pb-24 relative z-10 bg-midnight`}><Repertoire onPageChange={handlePageChange} /></div>;
      case 'media':
        return <div className={`${contentTopPadding} pb-24 relative z-10 bg-midnight`}><MediaSection activeTitle={activeMediaTitle} /></div>;
      case 'contatti':
        return <div className={`${contentTopPadding} pb-24 relative z-10 bg-midnight`}><ContactForm /></div>;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen font-sans selection:bg-gold selection:text-midnight flex flex-col bg-midnight">
      <Navbar activePage={currentPage} onPageChange={handlePageChange} isFirstVisit={isFirstVisit} />
      
      <main className={`flex-grow transition-all duration-500 ease-in-out ${isTransitioning ? 'opacity-0 transform translate-y-4' : 'opacity-100'}`}>
        {renderPage()}
      </main>

      {/* Pulsante Musica Background (solo in Home durante la prima visita o se attiva) */}
      {currentPage === 'home' && audioRef.current && (
        <button 
          onClick={toggleMusic}
          className="fixed top-28 lg:top-40 right-6 z-50 p-3 md:p-4 rounded-full border border-gold/40 bg-midnight/60 backdrop-blur-md text-gold hover:bg-gold hover:text-midnight transition-all duration-500 shadow-2xl group"
          title={isMuted ? "Attiva musica" : "Disattiva musica"}
        >
          <div className="relative">
            {isMuted ? <VolumeX size={20} className="md:w-6 md:h-6" /> : <Volume2 size={20} className="md:w-6 md:h-6" />}
            {!isMuted && (
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-gold"></span>
              </span>
            )}
          </div>
        </button>
      )}

      <div className="relative z-30">
        <Footer onPageChange={handlePageChange} />
      </div>
    </div>
  );
};

export default App;